

import classNames from 'classnames';
import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Session from "supertokens-auth-react/recipe/session";
import { globalUserState } from "../globalState";
import { removeBackgroundImage } from "../apiClient";

const TwoColumnLayout = ({
  prompt,
  setPrompt,
  style,
  setStyle,
  model,
  setModel,
  loading,
  handleGenerate,
  handleUpload,
  imageUrl,
  setImageUrl,
  imageId,
  imagePrompt,
  imageStyle,
  styles
}) => {
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDescription, setUploadDescription] = useState("");
  const [removingBg, setRemovingBg] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function checkSession() {
      const sessionExists = await Session.doesSessionExist();
      setIsLoggedIn(sessionExists);
      if (process.env.NODE_ENV === 'development') {
        console.log("Frontend: Session exists?", sessionExists);
      }
      if (sessionExists) {
        let accessTokenPayload = await Session.getAccessTokenPayloadSecurely();
        let fullName = accessTokenPayload.FullName;
        if (process.env.NODE_ENV === 'development') {
          console.log("Frontend: Access Token Payload:", accessTokenPayload, "Full Name:", fullName);
        }
        globalUserState.set({ fullName });
      }
    }
    checkSession();
  }, [navigate]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setLocalPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
      setPendingFile(file);
      setUploadDescription("");
      setShowUploadModal(true);
      e.target.value = "";
    }
  };

  const handleUploadConfirm = () => {
    if (!pendingFile) return;
    handleUpload(pendingFile, uploadDescription);
    setShowUploadModal(false);
    setPendingFile(null);
    setUploadDescription("");
  };

  const onGenerateClick = () => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    handleGenerate();
  };

  const displayUrl = localPreviewUrl || imageUrl;
  // Use server URL when available (needed for mockup); fallback to display so redirect works when image is visible
  const urlToPass = imageUrl || displayUrl;

  const onUseImageClick = () => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    if (!urlToPass) return;
    navigate('/products', {
      state: {
        selectedImage: {
          image_url: urlToPass,
          prompt: imagePrompt || prompt || 'User uploaded image',
          style: imageStyle || style || 'User Upload',
          ...(imageId != null && { id: imageId })
        }
      }
    });
  };

  const handleRemoveBackground = async () => {
    if (imageId == null) return;
    setRemovingBg(true);
    try {
      const data = await removeBackgroundImage(imageId);
      const newUrl = data?.image_url ?? data?.imageUrl;
      if (newUrl) setImageUrl(newUrl);
    } catch (err) {
      console.error('Remove background failed:', err);
      alert('Failed to remove background. Please try again.');
    } finally {
      setRemovingBg(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-8 p-4 md:p-8 bg-gray-50 min-h-screen tour-step-1 relative">
      {/* Auth Modal (Action Gate) */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all scale-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-6">
                <svg className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Create Your Account</h3>
              <p className="text-gray-500 mb-8">
                Sign up to generate unlimited AI designs, save your work, and order custom prints. It's free to start!
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => navigate("/auth?show=signup")}
                  className="w-full bg-primary hover:bg-blue-700 text-white font-bold py-3.5 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl"
                >
                  Sign Up Free
                </button>
                <button
                  onClick={() => navigate("/auth")}
                  className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold py-3.5 px-6 rounded-xl border border-gray-200 transition-colors"
                >
                  Log In
                </button>
              </div>

              <button
                onClick={() => setShowAuthModal(false)}
                className="mt-6 text-gray-400 hover:text-gray-600 text-sm font-medium"
              >
                Continue Browsing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload description modal */}
      {showUploadModal && pendingFile && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Add a description</h3>
            <p className="text-gray-500 text-sm mb-4">
              Describe this image so you can tell it apart from others later (e.g. &quot;Logo for band&quot;, &quot;Birthday design&quot;).
            </p>
            <textarea
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              placeholder="e.g. Band logo, birthday design, custom artwork"
              rows={3}
              className="w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary mb-6"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowUploadModal(false); setPendingFile(null); setUploadDescription(""); }}
                className="flex-1 py-2.5 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadConfirm}
                className="flex-1 bg-primary hover:bg-blue-700 text-white font-bold py-2.5 px-4 rounded-lg transition-colors"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Left Column: Controls */}
      <div className="w-full md:w-1/3 bg-white rounded-xl shadow-lg p-6 md:p-8 h-fit">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Create Your Design</h2>

        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50/90 p-4 text-sm text-amber-950">
          <p className="font-semibold text-amber-900 mb-1">Artwork &amp; AI disclaimer</p>
          <p className="text-amber-900/90 leading-relaxed">
            You are responsible for any image you use on a product—whether you generate it with our AI tools or upload your own—including making sure it does not infringe anyone else&apos;s copyrights, trademarks, or other rights.
            AI-generated images can resemble existing work; uploaded files must be yours to use or properly licensed.
            By generating or uploading, you acknowledge our{' '}
            <Link to="/ai-tool-terms" className="font-medium text-amber-800 underline hover:text-amber-950">
              Third-Party AI Tool Terms
            </Link>{' '}
            and general{' '}
            <Link to="/terms" className="font-medium text-amber-800 underline hover:text-amber-950">
              Terms of Service
            </Link>.
          </p>
        </div>

        <div className="mb-6 tour-step-2">
          <label htmlFor="prompt" className="block mb-2 font-semibold text-gray-700">Your Prompt</label>
          <textarea
            id="prompt"
            rows="4"
            placeholder="e.g., A majestic lion wearing a crown, cinematic style"
            className="w-full p-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-primary transition duration-200"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>

        {/* Model Selection UI */}
        <div className="mb-6">
          <p className="mb-3 font-semibold text-gray-700">Choose AI Model:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <label className="cursor-pointer flex-1">
              <input
                type="radio"
                name="model"
                value="local"
                checked={model === 'local'}
                onChange={() => setModel('local')}
                className="hidden"
              />
              <div className={classNames(
                "border rounded-lg p-3 text-center transition-all",
                {
                  "border-primary bg-blue-50 ring-2 ring-primary": model === 'local',
                  "border-gray-200 hover:border-blue-300": model !== 'local'
                }
              )}>
                <div className="font-bold text-gray-800">Standard</div>
                <div className="text-xs text-gray-500">Free (0 Credits)</div>
              </div>
            </label>

            <label className="cursor-pointer flex-1">
              <input
                type="radio"
                name="model"
                value="aimlapi"
                checked={model === 'aimlapi'}
                onChange={() => setModel('aimlapi')}
                className="hidden"
              />
              <div className={classNames(
                "border rounded-lg p-3 text-center transition-all",
                {
                  "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500": model === 'aimlapi',
                  "border-gray-200 hover:border-indigo-300": model !== 'aimlapi'
                }
              )}>
                <div className="font-bold text-indigo-900">Flux (AIMLAPI)</div>
                <div className="text-xs text-indigo-600">Crypto Friendly (1 Credit)</div>
              </div>
            </label>

            <label className="cursor-pointer flex-1">
              <input
                type="radio"
                name="model"
                value="deepai"
                checked={model === 'deepai'}
                onChange={() => setModel('deepai')}
                className="hidden"
              />
              <div className={classNames(
                "border rounded-lg p-3 text-center transition-all",
                {
                  "border-green-500 bg-green-50 ring-2 ring-green-500": model === 'deepai',
                  "border-gray-200 hover:border-green-300": model !== 'deepai'
                }
              )}>
                <div className="font-bold text-green-900">DeepAI</div>
                <div className="text-xs text-green-600">Economy (1 Credit)</div>
              </div>
            </label>
          </div>
        </div>

        {model === 'local' && (
          <div className="mb-6 tour-step-3">
            <p className="mb-3 font-semibold text-gray-700">Choose a style:</p>
            <div className="flex flex-wrap gap-3">
              {styles.map((option) => {
                const optionValue = option.toLowerCase();
                return (
                  <label key={option} htmlFor={`style-${optionValue}`} className="cursor-pointer">
                    <input
                      id={`style-${optionValue}`}
                      type="radio"
                      name="style"
                      value={optionValue}
                      className="hidden"
                      checked={style === optionValue}
                      onChange={() => setStyle(optionValue)}
                    />
                    <div
                      className={classNames(
                        'px-4 py-2 rounded-full border text-sm font-medium transition',
                        {
                          'bg-primary text-white border-primary': style === optionValue,
                          'bg-white text-gray-700 border-gray-300 hover:border-primary': style !== optionValue,
                        }
                      )}
                    >
                      {option}
                    </div>
                  </label>
                );
              })}

            </div>
          </div>
        )}

        <button
          onClick={onGenerateClick}
          disabled={loading || !prompt}
          className="w-full bg-primary hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed tour-step-4"
        >
          {loading ? 'Generating...' : 'Generate Image'}
        </button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-2 text-gray-500">OR</span>
          </div>
        </div>

        <div className='tour-step-5'>
          <label htmlFor="file-upload" className="w-full text-center cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-3 px-6 rounded-lg transition-colors border border-gray-300">
            Upload Your Own
          </label>
          <input id="file-upload" name="file-upload" type="file" className="sr-only" accept="image/*" onChange={handleFileChange} />
        </div>
      </div>

      {/* Right Column: Image Display */}
      <div className="w-full md:w-2/3">
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 md:sticky md:top-24 tour-step-6">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-xl z-10">
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-700 animate-pulse">Generating your masterpiece...</p>
                <p className="text-sm text-gray-500">This can take a moment.</p>
              </div>
            </div>
          )}
          <div className="aspect-w-1 aspect-h-1 w-full bg-gray-100 rounded-lg overflow-hidden">
            {displayUrl ? (
              <img
                src={displayUrl}
                alt="Generated design"
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  <h3 className="mt-2 text-sm font-semibold text-gray-900">Your generated image will appear here</h3>
                  <p className="mt-1 text-sm text-gray-500">Enter a prompt and click "Generate Image"</p>
                </div>
              </div>
            )}
          </div>
          {displayUrl && (
            <>
              {(imagePrompt || imageStyle) && (
                <div className="mt-4 text-sm text-gray-600 space-y-1">
                  {imagePrompt && <p><strong>Prompt:</strong> {imagePrompt}</p>}
                  {imageStyle && <p><strong>Style:</strong> {imageStyle}</p>}
                </div>
              )}
              <button
                type="button"
                onClick={handleRemoveBackground}
                disabled={loading || removingBg || imageId == null}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg transition-colors mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                title={imageId == null ? 'Preparing image for background removal...' : undefined}
              >
                {removingBg ? 'Removing background…' : 'Remove Background'}
              </button>
              <button
                type="button"
                onClick={onUseImageClick}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors mt-4 disabled:opacity-50 disabled:cursor-not-allowed tour-step-7"
                disabled={loading}
              >
                {loading ? 'Please wait…' : 'Use This Image'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoColumnLayout;
